
// @ts-ignore
    import __i18nConfig from '@next-translate-root/i18n'
// @ts-ignore
    import __loadNamespaces from 'next-translate/loadNamespaces'
// @ts-ignore
    
import { basicSEO } from 'helpers/jsx';
import useTranslation from 'next-translate/useTranslation';
import Head from 'next/head';
import { useRouter } from 'next/router';
import React, { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { getSharedApiClient } from '../../api/client';
import Cards from '../../components/Cards';
import Layout from '../../components/Layout';
import { useAuth, withNoSubscription } from '../../context/authContext';
import { ProvideStripe, useStripeCtx } from '../../context/stripeContext';
import { BuyType, CardType, Namespace, QueryType, AppApiError, StorageKeys } from 'enums';
import {
  countryFromHost,
  countryHasTrial,
  createFormSchema,
  formatDate,
  getNoTrialTranslationKeyIfNeeded,
  notEmptyRule,
  Permalink,
  removeErrorClass,
  resetForm,
  setLoadingState,
  validateForm,
} from '../../helpers';
import { Plan } from '../../typings/api';
import styles from './index.module.scss';
import { useNotificationContext } from 'context';
import { GetServerSidePropsContext } from 'next';
import { sleep } from 'api/apiHelpers';
import Analytics, { SEZNAM_CID_SUCCESS_SUBSCRIPTION } from 'helpers/Analytics';
import { StaticPathParams } from 'typings/staticPaths';
import { BrowseSkeleton } from 'components';
import Trans from 'next-translate/Trans';
import AppLink from 'components/AppLink/AppLink';
import { authorizeUserServerSide } from 'api/firebaseAdmin';
import { useLang, useLocalisedError } from 'context/langContext';
import prices from 'config/prices';
import { CodeType } from '../../typings/api/user-promo';
import { rewritePricesForDiscountedPlans } from '../../helpers/plans';
import StripeDisclaimer from 'components/StripeDisclaimer';
import storageHelper from 'helpers/storageHelper';

interface Props {
  plans: Plan[];
  priceYearlyDiscount?: string;
  type?: QueryType;
  countryCode: string;
  host: string;
}

let debounceTimer: number = null;

const ChoosePlan = ({ plans, priceYearlyDiscount, type, countryCode, host }: Props): JSX.Element => {
  const { t } = useTranslation(Namespace.ChoosePlan);
  const { createNotification } = useNotificationContext();
  const formRef = useRef(null);
  const router = useRouter();
  const { locale } = useLang();
  const { localisedError } = useLocalisedError();
  const { user, reloadUser } = useAuth();
  const serverError = useRef<HTMLLabelElement>(null);
  const [localPlans, setLocalPlans] = useState(plans);

  const [isTrialAvailable, userRelatedCountryCode] = useMemo(() => {
    const country = countryFromHost(host);
    return [countryHasTrial(country), country];
  }, [host]);

  console.log(localPlans);

  const [queryType, setQueryType] = useState<QueryType>(
    storageHelper.get(StorageKeys.HOMEPAGE_PLAN_TYPE) ?? type ?? QueryType.Monthly,
  );
  const [buyType, setBuyType] = useState(type === QueryType.Voucher ? BuyType.Voucher : BuyType.Card);
  const [selectedCard, setSelectedCard] = useState('');
  const [redirecting, setRedirecting] = useState(false);
  const [isPromoOpen, openPromo] = useState(false);
  const [submitDisabled, setSubmitDisabled] = useState(false);
  const [promoCode, setPromoCode] = useState<{ value: string; type?: CodeType }>({ value: '' });
  const [promoCodeError, setPromoCodeError] = useState<string>(null);
  const promoCodeRef = useRef(null);

  const { cardHasError, confirmCardSetup, stripeError, stripeCard, confirmCardPayment, createPaymentMethod } =
    useStripeCtx();

  const resetPromoCode = useCallback(() => {
    const promoCodeInput = promoCodeRef.current as HTMLInputElement;
    promoCodeInput.value = '';
    setPromoCode({ value: '' });
    setLocalPlans(plans);
    setSubmitDisabled(false);
    setPromoCodeError(null);
  }, [removeErrorClass, setPromoCode, setLocalPlans, setSubmitDisabled, promoCodeRef.current, plans]);

  const onPromoCodeBlur = useCallback(
    async (promoCodeValue: string) => {
      if (promoCodeValue.length > 0) {
        setSubmitDisabled(true);
        try {
          const paymentCodes = await getSharedApiClient().getPaymentCodes(promoCodeValue);
          setPromoCode({ value: promoCodeValue, type: paymentCodes.type });
          if ([CodeType.DISCOUNT, CodeType.PROMO].indexOf(paymentCodes.type) > -1) {
            const updatedPlans = rewritePricesForDiscountedPlans(paymentCodes.plans);
            setLocalPlans(updatedPlans);
          } else {
            throw AppApiError.CodeDoesNotExist;
          }
          removeErrorClass(formRef.current);
          setSubmitDisabled(false);
          setPromoCodeError(null);
        } catch (e) {
          console.log('e ', e);
          setPromoCodeError(localisedError(e as AppApiError));
        }
      } else {
        resetPromoCode();
      }
    },
    [
      getSharedApiClient,
      resetPromoCode,
      setPromoCode,
      promoCode,
      setLocalPlans,
      formRef,
      removeErrorClass,
      setSubmitDisabled,
    ],
  );

  useEffect(() => {
    if (promoCodeRef.current) {
      promoCodeRef.current.addEventListener('input', async function () {
        setSubmitDisabled(true);
        window.clearTimeout(debounceTimer);
        const promoCodeStr = promoCodeRef.current.value;
        if (promoCodeStr.length >= 0) {
          // resolve after 200ms (or clear within next key stroke)
          debounceTimer = window.setTimeout(async () => {
            // fetch server results
            try {
              await onPromoCodeBlur(promoCodeStr);
              // eslint-disable-next-line no-empty
            } catch (e) {}
          }, 500);
        }
      });
    }
  }, [promoCodeRef.current, onPromoCodeBlur]);

  const redirectHome = useMemo(
    () => async (queryString?: string) => {
      setRedirecting(true);
      const userHadSubscription = user.had_subscription;

      // wait for stripe <-> be user sync
      await sleep(1000);
      await reloadUser();

      if (userHadSubscription) {
        router.push(Permalink.root(user, locale, queryString));
      } else {
        router.push(Permalink.registerContentSettings(locale, queryString));
      }
    },
    [user],
  );

  const getVoucherInputCls = useMemo(() => {
    if (buyType === BuyType.Voucher) {
      return [styles.notVisible, styles.visible].join(' ');
    } else {
      return styles.notVisible;
    }
  }, [buyType]);

  const getBtnText = useMemo(() => {
    if (queryType === QueryType.Voucher) {
      return t('redeem_voucher');
    } else if (queryType === QueryType.Monthly) {
      return t('activate_monthly');
    } else {
      return t('activate_yearly');
    }
  }, [queryType]);

  const EMPTY_RULE = t('common:form.not_empty');

  const onSubmit = useCallback(
    async (event: React.FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      event.stopPropagation();

      const form = event.target as HTMLFormElement;

      const plan = form.plan.value;
      let valid = false;
      // VOUCHER branch
      if (plan === 'planVoucher') {
        const voucher = form.voucher.value;
        const formSchema = createFormSchema({
          voucher: notEmptyRule(EMPTY_RULE),
        });
        const validateObject = {
          voucher,
        };
        valid = validateForm(formSchema, validateObject, formRef.current);
        if (valid) {
          try {
            await getSharedApiClient().redeeemVoucher(voucher);
            createNotification({ title: t('subscription_activated') });
            Analytics.v2_subscriptionPurchaseWithCoupon(voucher);
            await redirectHome();
          } catch (e) {
            const voucherInput = (formRef.current as HTMLFormElement).elements['voucher'];
            voucherInput.classList.add('error');
            serverError.current.innerHTML = localisedError(e as AppApiError);
            resetForm(formRef.current);
          }
        } else {
          resetForm(formRef.current);
        }
        // BUY branch
      } else {
        if (promoCodeError) {
          return;
        }

        setLoadingState(formRef.current);
        const planCurrency = plan.split(':');
        let payMethod: string;
        let stripeCardError = false;

        if (selectedCard !== CardType.NewCard && selectedCard !== '') {
          // user choose saved card, try pay with it
          payMethod = selectedCard;
        } else {
          if (!cardHasError) {
            const respPay = await createPaymentMethod();
            if (respPay.error) {
              resetForm(formRef.current);
              stripeCard.current.classList.add('error');
              stripeError.current.innerHTML = respPay.error.message;
              stripeCardError = true;
            } else {
              payMethod = respPay.paymentMethod.id;
            }
          } else {
            resetForm(formRef.current);
            stripeCard.current.classList.add('error');
            stripeError.current.innerHTML = t('card_number_invalid');
            stripeCardError = true;
          }
        }
        if (!stripeCardError) {
          const planId = parseInt(planCurrency[0]);
          const subPlan = localPlans.find((p) => p.id === planId);

          const logEventToAnalytics = () => {
            if (user.had_subscription) {
              Analytics.v2_subscriptionRenewal(subPlan);
            } else {
              Analytics.v2_subscriptionPurchase(subPlan);
              if (isTrialAvailable && Boolean(subPlan.trial)) {
                Analytics.v2_trialStart();
              }
            }
          };

          try {
            const payload = {
              payment_method: payMethod ?? '',
              currency: subPlan.price.currency,
              plan_id: planId,
            };
            if (promoCode.value !== '') {
              if (promoCode.type === CodeType.DISCOUNT) {
                payload['discount_code'] = promoCode.value;
              } else if (promoCode.type === CodeType.PROMO) {
                payload['promo_code'] = promoCode.value;
              }
            }
            const res = await getSharedApiClient().buySubscription(payload);

            if (res.status === 'CARD_SETUP') {
              const checkout = await confirmCardSetup(res.client_secret, user.full_name);

              if (checkout.error) {
                resetForm(formRef.current);
                stripeCard.current.classList.add('error');
                stripeError.current.innerHTML = checkout.error.message;
              } else if (checkout.setupIntent.status === 'succeeded') {
                createNotification({ title: t('subscription_activated') });
                logEventToAnalytics();
                await redirectHome(`seznam_cId=${SEZNAM_CID_SUCCESS_SUBSCRIPTION}`);
              }
            } else if (res.status === 'ACTION_REQUIRED') {
              const paymentResp = await confirmCardPayment(
                res.payment_intent_client_secret,
                null,
                payload.payment_method,
              );
              if (paymentResp.error) {
                stripeCard.current.classList.add('error');
                stripeError.current.innerHTML = paymentResp.error.message;
                resetForm(formRef.current);
              } else {
                createNotification({ title: t('subscription_activated') });
                logEventToAnalytics();
                await redirectHome(`seznam_cId=${SEZNAM_CID_SUCCESS_SUBSCRIPTION}`);
              }
            } else {
              createNotification({ title: t('subscription_activated') });
              logEventToAnalytics();
              await redirectHome(`seznam_cId=${SEZNAM_CID_SUCCESS_SUBSCRIPTION}`);
            }
          } catch (e) {
            createNotification({ title: t('error_general') });
            resetForm(formRef.current);
          }
        }
      }
    },
    [
      cardHasError,
      confirmCardSetup,
      confirmCardPayment,
      createPaymentMethod,
      selectedCard,
      user,
      stripeError,
      stripeCard,
      promoCode,
      promoCodeError,
    ],
  );

  const promoCodeJSX = useMemo(() => {
    function onClick() {
      if (isPromoOpen) {
        resetPromoCode();
      }
      openPromo(!isPromoOpen);
    }
    return (
      <>
        <label className={[styles.promoCode, isPromoOpen ? styles.openPromo : ''].join(' ')}>
          <span onClick={onClick} />
          <p className={styles.promoText} onClick={onClick}>
            {t('advanced_form.use_promo_code')}
          </p>
          <input
            ref={promoCodeRef}
            placeholder={t('advanced_form.promo_code_placeholder')}
            type="text"
            name="promoCode"
            id="promoCode"
            className={promoCodeError ? 'error' : ''}
          />
          <span>{promoCodeError}</span>
        </label>
      </>
    );
  }, [promoCodeError, isPromoOpen, promoCode, promoCodeError]);

  const basicForm = (() => (
    <main className={styles.choosePlan}>
      <h1>{t('title')}</h1>
      {!user?.had_subscription && <p>{t(getNoTrialTranslationKeyIfNeeded('payment_info', userRelatedCountryCode))}</p>}
      <form ref={formRef} onSubmit={onSubmit}>
        <div className={['radios', styles.radios].join(' ')}>
          {localPlans?.map((plan, i) => (
            <Fragment key={plan.id}>
              <input
                type="radio"
                name="plan"
                value={`${plan.id}:${plan?.price?.currency}`}
                id={`${plan.id}`}
                defaultChecked={
                  ((type === QueryType.Monthly || !type) && i === 0) || (type === QueryType.Yearly && i === 1)
                }
                onChange={() => {
                  setBuyType(BuyType.Card);
                  if (plan?.recurrence.length === 1 && plan?.recurrence.unit === 'MONTH') {
                    setQueryType(QueryType.Monthly);
                  } else {
                    setQueryType(QueryType.Yearly);
                  }
                }}
              />
              <label className={['radio', styles.radio].join(' ')} htmlFor={`${plan.id}`}>
                <strong>{plan.title}</strong>
                {t('choose-plan:price_with_vat', { price: plan.price.formatted })}
              </label>
            </Fragment>
          ))}
          <input
            onChange={() => {
              setBuyType(BuyType.Voucher);
              setQueryType(QueryType.Voucher);
              setSelectedCard('');
              setLocalPlans(plans);
            }}
            type="radio"
            defaultChecked={type === QueryType.Voucher}
            name="plan"
            value="planVoucher"
            id="planVoucher"
          />
          <label className={['radio', styles.radio].join(' ')} htmlFor="planVoucher">
            <img src="/png/icGift.png" alt="gift" />
            <span className="gift">{t('got_voucher')}</span>
          </label>
        </div>
        <p className={styles.subInfo}>
          {!user?.needsToPay && t('payment_sub_info', { yearPriceDiff: priceYearlyDiscount })}
        </p>
        <label className={getVoucherInputCls}>
          {t('voucher_label')}
          <input placeholder={t('voucher_placeholder')} type="text" name="voucher" id="voucher" />{' '}
          <span ref={serverError}></span>
        </label>
        {buyType === BuyType.Card && (
          <>
            <Cards setSelected={setSelectedCard} selected={selectedCard} />
            {promoCodeJSX}
          </>
        )}
        <button type="submit" className="btn btn-primary">
          {getBtnText}
        </button>
        <StripeDisclaimer />
      </form>
    </main>
  ))();

  const advancedForm = !user?.showAdvancedChoosePlanForm
    ? null
    : (() => {
        if (buyType === BuyType.Voucher) {
          return (
            <main className={[styles.choosePlan, styles.advancedForm].join(' ')}>
              <div className={[styles.primary, styles.primaryVoucher].join(' ')}>
                <h1>{t('choose-plan:advanced_form.voucher_title')}</h1>
                <a
                  href="#"
                  onClick={(e) => {
                    e.preventDefault();
                    setBuyType(BuyType.Card);
                  }}
                >
                  {t('choose-plan:advanced_form.switch_to_card')}
                </a>
                <form ref={formRef} onSubmit={onSubmit}>
                  <input type="hidden" name="plan" value="planVoucher" id="planVoucher" />
                  <label className={getVoucherInputCls}>
                    {t('voucher_label')}
                    <input placeholder={t('voucher_placeholder')} type="text" name="voucher" id="voucher" />{' '}
                    <span ref={serverError}></span>
                  </label>
                  <button type="submit" className="btn btn-primary">
                    {t('common:form.activate')}
                  </button>
                </form>
              </div>
              <div className={[styles.secondary, styles.secondaryVoucher].join(' ')}>
                <h2>{t('choose-plan:advanced_form.voucher_description_title')}</h2>
                <p>{t('choose-plan:advanced_form.voucher_description_subtitle')}</p>
                <AppLink href={Permalink.voucher(locale)}>
                  <a>{t('choose-plan:advanced_form.voucher_description_action')}</a>
                </AppLink>
              </div>
            </main>
          );
        }

        const discount = `${Math.floor((+localPlans[1].price.value / 12 / +localPlans[0].price.value - 1) * 100)}%`;

        return (
          <main className={[styles.choosePlan, styles.advancedForm].join(' ')}>
            <div className={styles.primary}>
              <h1>{t('choose-plan:advanced_form.title')}</h1>
              <a
                href="#"
                onClick={(e) => {
                  e.preventDefault();
                  setBuyType(BuyType.Voucher);
                }}
              >
                {t('choose-plan:advanced_form.switch_to_voucher')}
              </a>
              <form ref={formRef} onSubmit={onSubmit}>
                <div className={['radios', styles.radios].join(' ')}>
                  {localPlans?.map((plan, i) => {
                    const isMonthly = plan.recurrence.unit === 'MONTH';
                    return (
                      <Fragment key={plan.id}>
                        <input
                          type="radio"
                          name="plan"
                          value={`${plan.id}:${plan?.price?.currency}`}
                          id={`${plan.id}`}
                          defaultChecked={
                            ((queryType === QueryType.Monthly || !queryType) && i === 0) ||
                            (queryType === QueryType.Yearly && i === 1)
                          }
                          onChange={() => {
                            setBuyType(BuyType.Card);
                            if (plan?.recurrence.length === 1 && isMonthly) {
                              setQueryType(QueryType.Monthly);
                            } else {
                              setQueryType(QueryType.Yearly);
                            }
                          }}
                        />
                        <label className={['radio', styles.radio].join(' ')} htmlFor={`${plan.id}`}>
                          <Trans
                            i18nKey={getNoTrialTranslationKeyIfNeeded(
                              `choose-plan:advanced_form.${isMonthly ? 'monthly' : 'yearly'}`,
                              userRelatedCountryCode,
                            )}
                            components={[<strong />]}
                            values={{ price_formatted: plan.price.formatted }}
                          />
                          {i === 1 && <em>{discount}</em>}
                        </label>
                      </Fragment>
                    );
                  })}
                </div>
                <label className={getVoucherInputCls}>
                  {t('voucher_label')}
                  <input placeholder={t('voucher_placeholder')} type="text" name="voucher" id="voucher" />{' '}
                  <span ref={serverError}></span>
                </label>
                <Cards setSelected={setSelectedCard} selected={selectedCard} />
                {promoCodeJSX}
                <button type="submit" disabled={submitDisabled} className="btn btn-primary">
                  {t(
                    getNoTrialTranslationKeyIfNeeded(
                      'choose-plan:advanced_form.button_activate',
                      userRelatedCountryCode,
                    ),
                  )}
                </button>
              </form>
            </div>
            {isTrialAvailable && (
              <div className={styles.secondary}>
                <h2>
                  <Trans
                    i18nKey={getNoTrialTranslationKeyIfNeeded(
                      'choose-plan:advanced_form.timeline_title',
                      userRelatedCountryCode,
                    )}
                    components={[<br />]}
                  />
                </h2>
                <ul className={styles.timeline}>
                  <li>
                    <Trans
                      i18nKey={getNoTrialTranslationKeyIfNeeded(
                        'choose-plan:advanced_form.timeline_step_1',
                        userRelatedCountryCode,
                      )}
                      components={[<strong />]}
                    />
                  </li>
                  <li>
                    <Trans
                      i18nKey={'choose-plan:advanced_form.timeline_step_4'}
                      values={{
                        date_formatted: formatDate(new Date(Date.now() + 5 * 24 * 60 * 60 * 1000), locale),
                      }}
                      components={[<strong />, <br />]}
                    />
                  </li>
                  <li>
                    <Trans
                      i18nKey={'choose-plan:advanced_form.timeline_step_2'}
                      components={[<strong />, <br />]}
                      values={{
                        date_formatted: formatDate(new Date(Date.now() + 7 * 24 * 60 * 60 * 1000), locale),
                      }}
                    />
                  </li>
                  <li>{t('choose-plan:advanced_form.timeline_step_3')}</li>
                </ul>
              </div>
            )}
          </main>
        );
      })();

  if (redirecting) {
    return <BrowseSkeleton countryCode={countryCode} />;
  }

  return (
    <Layout countryCode={countryCode} allowPopups={false}>
      <Head>{basicSEO(t('seo:choose_plan_title'), t('seo:choose_plan_description'))}</Head>
      {user && !user.showAdvancedChoosePlanForm && basicForm}
      {user && user.showAdvancedChoosePlanForm && advancedForm}
    </Layout>
  );
};

 async function _getServerSideProps(ctx: GetServerSidePropsContext<StaticPathParams>): Promise<unknown> {
  let type = ctx.query?.type as QueryType;
  const { user, locale, apiClient, countryCode } = await authorizeUserServerSide(ctx);

  if (user && user.isSubscriber && !user.needsToPay) {
    return {
      redirect: {
        permanent: false,
        destination: Permalink.collection(locale),
      },
    };
  } else if (!user) {
    return {
      redirect: {
        permanent: false,
        destination: Permalink.login(locale),
      },
    };
  }

  const allPlans = await apiClient.getPlans();
  let plans = allPlans.filter((p) => p.is_public && p.price && p.is_purchasable);

  if (user?.isInGracePeriod) {
    plans = plans.filter((p) => p.id === user.plan.id);
    type = (() => {
      switch (plans[0].recurrence.unit) {
        case 'WEEK':
          return QueryType.Weekly;
        case 'MONTH':
          return QueryType.Monthly;
        case 'YEAR':
          return QueryType.Yearly;
        default:
          return null;
      }
    })();
  }

  plans = rewritePricesForDiscountedPlans(plans);

  let priceMonthly: string;
  let priceYearlyDiscount: string;
  const planMonthly = plans.find((p) => p.recurrence.unit === 'MONTH' && p.recurrence.length === 1);

  if (planMonthly) {
    priceMonthly = planMonthly.price.formatted;
    const currency = planMonthly.price.currency;
    priceYearlyDiscount = priceMonthly
      .replace(',', '.')
      .replace('0', '')
      .replace(planMonthly.price.value.toString(), prices[currency].yearPriceDiff.toString());
  }

  if (![QueryType.Weekly, QueryType.Monthly, QueryType.Yearly, QueryType.Voucher].includes(type)) {
    type = null;
  }

  return {
    props: { plans, priceYearlyDiscount, type, countryCode, host: ctx.req.headers.host },
  };
}

export default withNoSubscription(ProvideStripe(ChoosePlan));


// @ts-ignore
    export async function getServerSideProps(ctx) {
// @ts-ignore
        let res = _getServerSideProps(ctx)
// @ts-ignore
        if(typeof res.then === 'function') res = await res
// @ts-ignore
        return {
// @ts-ignore
          
// @ts-ignore
          ...res,
// @ts-ignore
          props: {
// @ts-ignore
            ...(res.props || {}),
// @ts-ignore
            ...(await __loadNamespaces({
// @ts-ignore
              ...ctx,
// @ts-ignore
              pathname: '/choose-plan/index',
// @ts-ignore
              loaderName: 'getServerSideProps',
// @ts-ignore
              ...__i18nConfig,
// @ts-ignore
              
// @ts-ignore
            }))
// @ts-ignore
          }
// @ts-ignore
        }
// @ts-ignore
    }
// @ts-ignore
  